import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNearbyStore } from '../data/geo';
import { createStore, useStoreResolver, useStores, type StoreView } from '../data/stores';
import { formatDistance } from '../lib/geo';
import { BottomSheet } from './BottomSheet';
import { secondaryBtn } from './ui/classes';
import { SkeletonRows } from './ui/Skeleton';
import { StoreForm } from './ui/StoreForm';
import { useStoreDetails } from './ui/useStoreDetails';

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (storeId: string) => void;
  title?: string;
  /** Aktualnie wybrany sklep — wyróżniony na liście. */
  selectedId?: string | null;
  /** Sklep pominięty na liście (np. ten, który łączymy z innym). */
  excludeId?: string | null;
  /** Formularz „Dodaj sklep” na dole (domyślnie tak). */
  allowAdd?: boolean;
  /** Te sklepy na górze listy (np. ta sama sieć); w obrębie grup kolejność bez zmian. */
  prioritize?: ((store: StoreView) => boolean) | undefined;
  /** Dopisek pod nazwą, po opisie sklepu (np. wielkość trasy). */
  hint?: ((store: StoreView) => string | null) | undefined;
}

/**
 * Wybór sklepu w arkuszu: ulubione na górze, na dole szybkie dodanie nowego.
 * Po wyborze wywołuje onPick(id), a potem onClose(). Renderowany w portalu, więc można go wstawić wszędzie.
 */
export function StorePicker({
  open,
  onClose,
  onPick,
  title = 'Wybierz sklep',
  selectedId = null,
  excludeId = null,
  allowAdd = true,
  prioritize,
  hint,
}: Props) {
  const pick = (id: string) => {
    onPick(id);
    onClose();
  };
  return createPortal(
    <BottomSheet open={open} onClose={onClose} title={title}>
      <PickerBody onPick={pick} selectedId={selectedId} excludeId={excludeId} allowAdd={allowAdd} prioritize={prioritize} hint={hint} />
    </BottomSheet>,
    document.body,
  );
}

interface BodyProps {
  onPick: (id: string) => void;
  selectedId: string | null;
  excludeId: string | null;
  allowAdd: boolean;
  prioritize: Props['prioritize'];
  hint: Props['hint'];
}

function PickerBody({ onPick, selectedId, excludeId, allowAdd, prioritize, hint }: BodyProps) {
  const all = useStores();
  const details = useStoreDetails(all);
  // Id mogą wskazywać na scalony duplikat — porównujemy po sklepie, który został.
  const resolve = useStoreResolver() ?? ((id: string) => id);
  const near = useNearbyStore();
  const [adding, setAdding] = useState(false);

  if (all === undefined) return <SkeletonRows count={2} />;
  const selected = selectedId ? resolve(selectedId) : null;
  const excluded = excludeId ? resolve(excludeId) : null;
  const stores = all.filter((s) => s.id !== excluded);
  // Pozycja znana: najbliższe na górze, sklepy bez położenia na końcu w kolejności z useStores.
  if (near.position) stores.sort((a, b) => compareDistance(near.distances.get(a.id), near.distances.get(b.id)));
  // Sortowanie stabilne: w obu grupach zostaje dotychczasowa kolejność.
  if (prioritize) stores.sort((a, b) => Number(prioritize(b)) - Number(prioritize(a)));
  const showForm = allowAdd && (adding || stores.length === 0);

  return (
    <div className="flex flex-col gap-3">
      {stores.length > 0 ? (
        <ul className="-mx-2 flex flex-col">
          {stores.map((s) => {
            const isSelected = s.id === selected;
            const info = [details(s), hint?.(s)].filter(Boolean).join(' · ');
            const distance = near.distances.get(s.id);
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onPick(s.id)}
                  aria-current={isSelected || undefined}
                  className={`flex min-h-14 w-full items-center gap-3 rounded-xl px-2 py-2 text-left active:bg-stone-100 dark:active:bg-stone-800 ${
                    isSelected ? 'bg-brand-50 dark:bg-brand-800/20' : ''
                  }`}
                >
                  <span aria-hidden className={`w-6 shrink-0 text-center text-lg ${s.favorite ? 'text-amber-500' : 'text-transparent'}`}>
                    ★
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{s.name || 'Bez nazwy'}</span>
                    {info && <span className="block truncate text-sm text-stone-500">{info}</span>}
                  </span>
                  {distance !== undefined && <span className="shrink-0 text-sm text-stone-500">{formatDistance(distance)}</span>}
                  {isSelected && <span className="shrink-0 font-semibold text-brand-700 dark:text-brand-500">✓</span>}
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        !allowAdd && <p className="py-4 text-center text-stone-500">Nie ma innych sklepów.</p>
      )}

      {showForm ? (
        <div className="rounded-2xl border border-stone-200 p-3 dark:border-stone-800">
          <h3 className="mb-2 font-semibold">Dodaj sklep</h3>
          <StoreForm
            autoFocus={adding}
            submitLabel="Dodaj i wybierz"
            onSubmit={async (d) => onPick(await createStore({ name: d.name, chainName: d.chainName }))}
          />
        </div>
      ) : (
        allowAdd && (
          <button type="button" onClick={() => setAdding(true)} className={secondaryBtn}>
            + Dodaj sklep
          </button>
        )
      )}
    </div>
  );
}

/** Rosnąco po odległości; sklepy bez położenia (undefined) na końcu, między sobą bez zmian. */
function compareDistance(a: number | undefined, b: number | undefined): number {
  if (a === undefined || b === undefined) return Number(a === undefined) - Number(b === undefined);
  return a - b;
}
